import throttle from 'lodash.throttle';
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LocaleContext, isRtl } from '../../../i18n/src';
/**
 * Returns a series of variables to help support sticky headers in tables that also
 * need horizontal scrolling.
 */
export const useStickyHeader = () => {
    const [isStickyShowing, setIsStickyShowing] = useState(false);
    const tableContainerRef = useRef(null);
    const actionBarContainerRef = useRef(null);
    const xScrollingRef = useRef(null);
    const stickyTableRef = useRef(null);
    const defaultTableRef = useRef(null);
    const locale = useContext(LocaleContext);
    /**
     * since the main table and the table with the sticky header are separate from each other, we need to sync up the header widths of the
     * sticky headers to the current widths of the table header cells.
     */
    const updateTableHeaderWidths = () => {
        if ((defaultTableRef === null || defaultTableRef === void 0 ? void 0 : defaultTableRef.current) && (stickyTableRef === null || stickyTableRef === void 0 ? void 0 : stickyTableRef.current)) {
            const stickyHeaderCells = stickyTableRef.current.querySelectorAll('th');
            const defaultHeaderCells = defaultTableRef.current.querySelectorAll('th');
            stickyTableRef.current.style.width = `${defaultTableRef.current.offsetWidth}px`;
            stickyHeaderCells.forEach((cell, i) => {
                cell.style.width = `${defaultHeaderCells[i].offsetWidth}px`;
            });
        }
    };
    /**
     * Similar to the sticky header, the sticky action bar needs its width updated to match the table container
     */
    const updateActionBarWidth = () => {
        if ((tableContainerRef === null || tableContainerRef === void 0 ? void 0 : tableContainerRef.current) && (actionBarContainerRef === null || actionBarContainerRef === void 0 ? void 0 : actionBarContainerRef.current)) {
            const tableContainerRect = tableContainerRef.current.getBoundingClientRect();
            actionBarContainerRef.current.style.width = `${tableContainerRect.width}px`;
        }
    };
    /**
     * The sticky table with position:fixed will not horizontally scroll on its own, so we update its scroll here.
     */
    const xScrollHandler = useCallback(() => {
        if ((xScrollingRef === null || xScrollingRef === void 0 ? void 0 : xScrollingRef.current) && (stickyTableRef === null || stickyTableRef === void 0 ? void 0 : stickyTableRef.current)) {
            stickyTableRef.current.style.marginInlineStart = `${isRtl(locale) ? '' : '-'}${xScrollingRef.current.scrollLeft}px`;
        }
    }, [locale]);
    useEffect(() => {
        const horizScrollingRef = xScrollingRef === null || xScrollingRef === void 0 ? void 0 : xScrollingRef.current;
        horizScrollingRef === null || horizScrollingRef === void 0 ? void 0 : horizScrollingRef.addEventListener('scroll', xScrollHandler, {
            passive: true,
        });
        return () => {
            horizScrollingRef === null || horizScrollingRef === void 0 ? void 0 : horizScrollingRef.removeEventListener('scroll', xScrollHandler);
        };
    }, [xScrollHandler]);
    useEffect(() => {
        const scrollHandler = () => {
            if (tableContainerRef === null || tableContainerRef === void 0 ? void 0 : tableContainerRef.current) {
                const tableContainerRect = tableContainerRef.current.getBoundingClientRect();
                // if the top of the container is past the visible viewport
                if (tableContainerRect.top < 0 && tableContainerRect.bottom > 0) {
                    setIsStickyShowing(true);
                }
                else if (tableContainerRect.top > 0 || tableContainerRect.bottom < 0) {
                    setIsStickyShowing(false);
                }
            }
        };
        window.addEventListener('scroll', scrollHandler, { passive: true });
        return () => {
            window.removeEventListener('scroll', scrollHandler);
        };
    }, []);
    useLayoutEffect(() => {
        if (isStickyShowing) {
            updateTableHeaderWidths();
            updateActionBarWidth();
        }
        else {
            if (actionBarContainerRef === null || actionBarContainerRef === void 0 ? void 0 : actionBarContainerRef.current) {
                actionBarContainerRef.current.style.width = '100%';
            }
        }
    }, [isStickyShowing]);
    useLayoutEffect(() => {
        // Since the sticky header is removed from document flow, we use a ResizeObserver here to properly update the header width
        // when the container is resized, so that header cells and the action bar remain in sync with table body cells
        if (!(tableContainerRef === null || tableContainerRef === void 0 ? void 0 : tableContainerRef.current)) {
            return;
        }
        let observer;
        if (isStickyShowing) {
            // throttle the resize observer to only trigger its callback once every 16ms
            observer = new ResizeObserver(throttle(() => {
                updateTableHeaderWidths();
                updateActionBarWidth();
            }, 16));
            observer.observe(tableContainerRef.current);
        }
        return () => observer === null || observer === void 0 ? void 0 : observer.disconnect();
    }, [isStickyShowing]);
    return {
        isStickyShowing, // use this boolean to conditionally show the sticky header
        tableContainerRef, // set this ref as the main div container of the table
        actionBarContainerRef, // set this ref as the div container of the table action bar
        xScrollingRef, // set this ref for the horizontal scrolling container
        stickyTableRef, // set this ref for the table that contains the sticky header
        defaultTableRef, // set this ref for the table that contains the data and default header
    };
};
//# sourceMappingURL=use_sticky_header.js.map